import type { Webcast } from '../../../../generated/graphql-manager';

import { Heading, notification } from '@movingimage-evp/mi-ui-component-library';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { DescriptionEditors } from './description-editors';
import { EventSpeakers, useSpeakers } from './speakers';
import {
  State,
  useGetWebcastDescriptionQuery,
  useUpdateWebcastDescriptionMutation,
} from '../../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../../routes';
import { isRichTextEditorContentEmpty, slateToHtml } from '../../../../utils';
import { SetupPageFooter } from '../../../components/setup-page-footer';
import { useUserPermissions } from '../../../hooks/user-permissions';

import managerStyles from '../../../manager.module.css';

// Compares a stored description (legacy Slate JSON or HTML) against the editor's current HTML.
// Empty-equivalent content is treated as unchanged so an empty editor (`<p></p>`) doesn't read as a
// change against empty/absent stored content (`''`/`null`).
function descriptionChanged(stored: string | null | undefined, current: string | undefined) {
  if (isRichTextEditorContentEmpty(stored) && isRichTextEditorContentEmpty(current)) return false;
  return slateToHtml(stored) !== current;
}

export function EventDescriptionPage() {
  const { webcastId = '' } = useParams();
  const { t } = useTranslation();
  const routes = useAbsoluteRoutes();
  const { isEventEditingAllowed } = useUserPermissions();

  const [savingFailed, setSavingFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorRefreshKeys, setEditorRefreshKeys] = useState([Math.random(), Math.random(), Math.random()]);

  const getWebcastDescriptions = (contents: Webcast['contents']) => {
    setDescriptions(
      contents?.map((content) => ({
        language: content.language,
        preLive: slateToHtml(content.preLive?.description),
        live: slateToHtml(content.live?.description),
        postLive: slateToHtml(content.postLive?.description),
      })) || []
    );

    // Force the accordions/editors to remount so they pick up the freshly loaded content.
    setEditorRefreshKeys([Math.random(), Math.random(), Math.random()]);
  };

  const { data, loading } = useGetWebcastDescriptionQuery({
    variables: { webcastId },
    skip: !webcastId,
    onCompleted(data) {
      getWebcastDescriptions(data?.webcast?.contents);
    },
  });

  const webcast = data?.webcast;
  const archivedWebcast = webcast?.state === State.ARCHIVED;
  const isEventEditable = isEventEditingAllowed && !archivedWebcast;
  const isLiveOrLiveTest = webcast?.state === State.LIVE || webcast?.state === State.LIVE_TEST;
  const primaryLanguage = webcast?.primaryLang;
  const additionalLanguages = webcast?.additionalLangs || [];
  const languages = [primaryLanguage, ...additionalLanguages]
    .filter((language) => language !== undefined)
    .filter((language) => language.__typename === 'StandardLanguage');

  const [descriptions, setDescriptions] = useState(
    webcast?.contents.map((content) => ({
      language: content.language,
      preLive: slateToHtml(content.preLive?.description),
      live: slateToHtml(content.live?.description),
      postLive: slateToHtml(content.postLive?.description),
    })) || []
  );

  const eventDescription = (language: string, type: 'preLive' | 'live' | 'postLive') => {
    return descriptions.find((description) => description.language === language)?.[type];
  };

  const handleDescriptionChange = (value: string, language: string, type: 'preLive' | 'live' | 'postLive') => {
    setDescriptions((prevDescriptions) =>
      prevDescriptions.map((description) => {
        if (description.language === language) {
          return {
            ...description,
            [type]: value,
          };
        }

        return description;
      })
    );
  };

  const [updateWebcastMutation] = useUpdateWebcastDescriptionMutation();

  const speakers = useSpeakers({
    webcastId,
    serverSpeakers: webcast?.speakers,
    disabled: !isEventEditingAllowed || archivedWebcast,
  });

  const [unsavedChanges, setUnsavedChanges] = useState<
    Record<string, { prelive: boolean; live: boolean; postlive: boolean }>
  >({});

  useEffect(() => {
    // Users who can't edit (role restriction, archived event) have no way to act on an "unsaved changes" hint, so don't compute it
    if (webcast?.contents && descriptions.length > 0 && isEventEditable) {
      webcast.contents.forEach((content, idx) => {
        const current = descriptions[idx];
        const language = content.language;
        const preLiveChanged = descriptionChanged(content.preLive?.description, current?.preLive);

        if (preLiveChanged !== unsavedChanges[language]?.prelive) {
          setUnsavedChanges((prev) => ({
            ...prev,
            [language]: {
              ...prev[language],
              prelive: preLiveChanged,
            },
          }));
        }

        const liveChanged = descriptionChanged(content.live?.description, current?.live);
        if (liveChanged !== unsavedChanges[language]?.live) {
          setUnsavedChanges((prev) => ({
            ...prev,
            [language]: {
              ...prev[language],
              live: liveChanged,
            },
          }));
        }

        const postLiveChanged = descriptionChanged(content.postLive?.description, current?.postLive);
        if (postLiveChanged !== unsavedChanges[language]?.postlive) {
          setUnsavedChanges((prev) => ({
            ...prev,
            [language]: {
              ...prev[language],
              postlive: postLiveChanged,
            },
          }));
        }
      });
    }
  }, [descriptions, unsavedChanges, webcast?.contents, isEventEditable]);

  // One Save persists the whole page in a single updateWebcast call. The BE uses optimistic locking,
  // so two parallel writes to the same webcast conflict — descriptions and speakers must go together.
  // Speakers are included only when they actually changed (an omitted input field is left untouched),
  // and any locally-picked images are uploaded first so the request carries their final asset ids.
  const saveChanges = async () => {
    if (!speakers.validate()) {
      setSavingFailed(true);
      return;
    }

    setSaving(true);

    try {
      const speakerInputs = speakers.hasUnsavedChanges ? await speakers.buildSpeakerInputs() : undefined;

      const response = await updateWebcastMutation({
        variables: {
          input: {
            id: webcastId,
            contents: data?.webcast.contents?.map((content) => ({
              language: content.language,
              preLive: { description: eventDescription(content.language, 'preLive') ?? '' },
              live: { description: eventDescription(content.language, 'live') ?? '' },
              postLive: { description: eventDescription(content.language, 'postLive') ?? '' },
            })),
            ...(speakerInputs && { speakers: speakerInputs }),
          },
        },
      });

      if (response.data?.updateWebcast.__typename === 'UpdateWebcastSuccess') {
        getWebcastDescriptions(response.data.updateWebcast.webcast.contents);
        setUnsavedChanges({});
        setSavingFailed(false);
      } else {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        setSavingFailed(true);
      }
    } catch {
      notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      setSavingFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = useCallback(() => {
    setEditorRefreshKeys([Math.random(), Math.random(), Math.random()]);
  }, []);

  return (
    <main className={managerStyles.main} data-testid="event-description-page">
      <Heading className={managerStyles.grayText}>{t('manager.webcastSetup.eventDescription.title')}</Heading>

      {!loading && (
        <>
          <DescriptionEditors
            primaryLanguage={primaryLanguage}
            languages={languages}
            disabled={archivedWebcast}
            unsavedChanges={unsavedChanges}
            editorRefreshKeys={editorRefreshKeys}
            getDescription={eventDescription}
            onDescriptionChange={handleDescriptionChange}
            onTabChange={handleTabChange}
          />

          <EventSpeakers
            speakers={speakers}
            disabled={!isEventEditingAllowed || archivedWebcast}
            deleteDisabled={isLiveOrLiveTest}
          />

          <SetupPageFooter
            nextLabel={t('manager.webcastSetup.eventDescription.nextStep.label')}
            route={routes.webcastSetup_media}
            showSaveButton
            saveButtonDisabled={!isEventEditingAllowed || archivedWebcast}
            saving={saving}
            savingFailed={savingFailed}
            onSaveButtonClick={saveChanges}
          />
        </>
      )}
    </main>
  );
}
